import React, { useState, useEffect, useRef } from 'react';
import List from '../components/List';
import '../styles/pages/record-info.css';
import Select from '../components/Select';
import Button from '../components/Button';
import MantinePagination from '../components/MantinePagination';
import MantineSelectBox from '../components/MantineSelectBox';
import MantineDatePicker from '../components/MantineDatePicker';
import { apiRequest } from '../utils/api';
import { BASE_URL } from '../Config';
import { Modal } from '@mantine/core';
import filterIcon from '../assets/icons/filter-icon.svg';
import Alert from '../components/Alert';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import InfoDisplay from '../components/InfoDisplay';

const RecordInfo = () => {
  const headers = ['순찰일시', '태그NO', '현장명', '순찰자', '상태', '보기'];
  const isMobile = window.innerWidth <= 768;
  const patrolListColumnWidths = isMobile ? ['130px', '150px', '110px', '80px', '60px', '60px'] : ['20%', '20%', '20%', '15%', '10%', '5%'];
  const stateOptions = [
    { value: '', label: '전체' },
    { value: 'A', label: '완료' },
    { value: 'C', label: '등록' },
  ];
  const countOptions = [
    { value: '20', label: '20개' },
    { value: '50', label: '50개' },
    { value: '100', label: '100개' },
  ];

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tagNo, setTagNo] = useState('');
  const [userName, setUserName] = useState('');
  const [siteName, setSiteName] = useState('');
  const [status, setStatus] = useState('');
  const [allData, setAllData] = useState([]);
  const [pagedData, setPagedData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSMSModalOpen, setIsSMSModalOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [alertOpen, setAlertOpen] = useState({ isOpen: false, message: '' });
  const [checkedIndices, setCheckedIndices] = useState([]);
  const selectedRows = checkedIndices.map(i => pagedData[i]);
  const [recordDetail, setRecordDetail] = useState(null);
  const reportRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [mobileFilterModalOpen, setMobileFilterModalOpen] = useState(false);

  useEffect(() => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);

    setStartDate(weekAgo);
    setEndDate(today);

    handleSearch();
  }, []);

  const handleSearch = async () => {
    try {
      const res = await apiRequest('/searchRecords', 'POST', {
        startDate,
        endDate,
        tagNo,
        userName,
        siteName,
        status,
      });

      const refinedData = res.data.map(user => {
        const STATE_MAP = {
          A: '완료',
          C: '등록',
        };
        const storePhone = user.STORE_PHONE;
        setPhoneNumber(storePhone);

        return {
          REG_DTM: user.REG_DTM,
          TAGNUM: user.TAGNUM,
          STORE_NAME: user.STORE_NAME,
          USER_NAME: user.USER_NAME,
          STATE: STATE_MAP[user.STATE] || '알수없음',
          VIEW: (
            <Button style={{ background: '#F9F9F9', color: '#3d3d3d', padding: '5px 20px' }} onClick={() => handleViewRecord(user)}>
              보기
            </Button>
          ),
        };
      });

      setAllData(refinedData);
      setCurrentPage(1);
    } catch (err) {
      console.error('조회 실패:', err);
      alert('조회 중 오류가 발생했습니다.');
    }
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleViewRecord = async user => {
    const id_his = user.ID_HIS;
    try {
      const res = await apiRequest('/getRecordDetail', 'POST', { ID: id_his });
      setRecordDetail(res.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('상세 조회 실패:', error);
      showAlert('상세 정보를 불러오는 데 실패했습니다.');
    }
  };

  const showAlert = message => {
    setAlertOpen({ isOpen: true, message });
  };
  const closeAlert = () => {
    setAlertOpen({ isOpen: false, message: '' });
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setRecordDetail(null);
  };

  const handleSMSModalOpen = () => {
    setIsSMSModalOpen(true);
  };

  const handleSMSModalClose = () => {
    setIsSMSModalOpen(false);
  };

  const handleSMSSend = async () => {
    if (!recordDetail || !recordDetail.checklistData) {
      showAlert('리포트 데이터가 없습니다.');
      return;
    }

    try {
      console.log('handleSMSSend start');
      setIsSending(true);

      const res = await apiRequest('/sendReportMMS', 'POST', {
        to: phoneNumber,
        record: recordDetail,
      });

      if (res.success) {
        showAlert('문자가 성공적으로 전송되었습니다.');
        handleSMSModalClose();
        setPhoneNumber('');
      } else {
        showAlert('문자 전송에 실패했습니다.');
      }
    } catch (err) {
      console.error('SMS 전송 중 오류:', err);
      showAlert('문자 전송 중 오류가 발생했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setPagedData(allData.slice(start, end));
  }, [allData, currentPage, itemsPerPage]);

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const excelDownload = async () => {
    const selected = checkedIndices.map(i => pagedData[i]);
    if (selected.length === 0) {
      showAlert('선택된 항목이 없습니다.');
      return;
    }

    const res = await fetch(`${BASE_URL}/download/patrol`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: selected }),
    });

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '순찰목록.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePhoneChange = e => {
    let value = e.target.value.replace(/[^0-9]/g, '');

    if (value.length <= 3) {
      setPhoneNumber(value);
    } else if (value.length <= 7) {
      setPhoneNumber(value.slice(0, 3) + '-' + value.slice(3));
    } else {
      setPhoneNumber(value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11));
    }
  };

  const handleSaveImage = async () => {
    if (!recordDetail) return;

    const element = reportRef.current;
    if (!element) {
      showAlert('출력 대상이 없습니다.');
      return;
    }

    try {
      // 잠깐 DOM을 보이게 만듬 (혹은 offscreen 위치에 있어야 함)
      element.style.display = 'block';

      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: true,
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let adjustedWidth = imgWidth;
      let adjustedHeight = imgHeight;

      if (imgHeight > pageHeight) {
        const ratio = pageHeight / imgHeight;
        adjustedWidth = imgWidth * ratio;
        adjustedHeight = imgHeight * ratio;
      }

      const x = (pageWidth - adjustedWidth) / 2;
      const y = (pageHeight - adjustedHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, adjustedWidth, adjustedHeight);
      pdf.save(`순찰기록_${recordDetail?.memoRow?.REG_DTM || 'report'}.pdf`);

      element.style.display = 'none'; // 다시 숨김
    } catch (err) {
      console.error('PDF 저장 오류:', err);
      showAlert('PDF 저장 중 오류가 발생했습니다.');
    }
  };

  const handleMobileFilterSearch = () => {
    handleSearch();
    setMobileFilterModalOpen(false);
  };

  return (
    <div className="record-info-page-content">
      <div className="record-top-section">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <p className="title-text gmarket-font">순찰목록</p>
          <img src={filterIcon} alt="필터아이콘" className="filter-icon" onClick={() => setMobileFilterModalOpen(true)} />
        </div>

        <div className="filter-section desktop-filter">
          <div className="filtering">
            <div className="date-filter-wrapper">
              <MantineDatePicker value={startDate} onChange={setStartDate} />
              <span style={{ margin: '10px 8px' }}>-</span>
              <MantineDatePicker value={endDate} onChange={setEndDate} />
            </div>

            <input
              placeholder="태그NO"
              value={tagNo}
              onChange={e => setTagNo(e.target.value)}
              onKeyDown={handleKeyDown}
              className="site-search-input"
              style={{ paddingLeft: '20px', width: '145px', height: '50px' }}
            />

            <input
              placeholder="회원명"
              value={userName}
              onChange={e => setUserName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="site-search-input"
              style={{ paddingLeft: '20px', width: '145px', height: '50px' }}
            />

            <input
              placeholder="사업장명"
              value={siteName}
              onChange={e => setSiteName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="site-search-input"
              style={{ paddingLeft: '20px', width: '145px', height: '50px' }}
            />

            <MantineSelectBox style={{ width: '100px' }} value={status} onChange={setStatus} withinPortal={false} options={stateOptions}>
              상태
            </MantineSelectBox>
          </div>

          <div className="action-section">
            <Button width="120px" height="50px" onClick={excelDownload}>
              엑셀출력
            </Button>
            <Button width="120px" height="50px" onClick={handleSearch}>
              찾기
            </Button>
          </div>
        </div>
      </div>

      <div className="list-wrapper patrol-list-wrapper">
        <List
          headers={headers}
          data={pagedData}
          showCheckbox={true}
          onItemCheck={(index, checked) => {
            setCheckedIndices(prev => {
              if (checked) return [...prev, index];
              return prev.filter(i => i !== index);
            });
          }}
          columnWidths={patrolListColumnWidths}
        />
      </div>

      <div className="middle-section">
        <div></div>
        <MantinePagination total={Math.ceil(allData.length / itemsPerPage)} value={currentPage} onChange={handlePageChange} />
        <div className="select-wrapper">
          <Select
            options={countOptions}
            width="107px"
            height="42px"
            value={itemsPerPage.toString()}
            onChange={e => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            출력보기
          </Select>
        </div>
      </div>

      <Modal opened={isModalOpen} onClose={() => setIsModalOpen(false)} zIndex={9998} centered size="xl">
        <div className="record-modal-container">
          <div className="modal-section">
            <div className="modal-background-wrapper">
              <span className="modal-big-title">DAEDEOK PATROL REPORT</span>
              <div className="info-display">
                <InfoDisplay label="순찰시간" value={recordDetail?.memoRow?.REG_DTM} />
                <InfoDisplay label="태크명" value={recordDetail?.memoRow?.TAGNUM} />
                <InfoDisplay label="사업장명" value={recordDetail?.memoRow?.STORE_NAME} />
                <InfoDisplay label="위치장소" value={recordDetail?.memoRow?.STORE_ADDR} />
              </div>
            </div>

            <div className="modal-background-wrapper">
              <div className="title-logo-wrapper">
                <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
                <p className="report-modal-title gmarket-font">점검결과 및 내용</p>
              </div>
              <div className="checklist-items-wrapper">
                {recordDetail?.checklistData?.length > 0 ? (
                  recordDetail.checklistData.map((item, index) => (
                    <div key={index} className="checklist-item">
                      <div className="checklist-header">
                        <div className={`custom-checkbox ${item.checked ? 'checked' : ''}`}>
                          {item.checked && <span className="checkmark"></span>}
                        </div>
                        <span className="checklist-content">{item.content}</span>
                      </div>
                      {item.reason && (
                          <div className="checklist-reason">
                            <strong>사유: </strong>
                            {item.reason}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-data gmarket-font">점검사항이 없습니다.</div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-background-wrapper">
            <div className="title-logo-wrapper">
              <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
              <p className="report-modal-title gmarket-font">전달사항 및 의견</p>
            </div>
            <div className="special-notes">{recordDetail?.memoRow ? recordDetail.memoRow.MEMO : <div className="no-images gmarket-font">특이사항이 없습니다.</div>}</div>
          </div>

          <div className="modal-background-wrapper">
            <div className="title-logo-wrapper">
              <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
              <p className="report-modal-title gmarket-font">점검 증빙사진 (1장)</p>
            </div>
            <div className="attached-images">
              {recordDetail?.attachedImages?.length > 0 ? (
                <>
                  {recordDetail.attachedImages.map((image, index) => (
                    <div key={index} className="image-item">
                      <img
                        src={`/public${image.url || image}`}
                        alt={`첨부사진 ${index + 1}`}
                        className="attached-image"
                        onClick={() => window.open(`/public${image.url || image}`, '_blank')}
                      />
                    </div>
                  ))}
                  <img src="/assets/images/dd-korean-logo.png" alt="로고" className="report-korean-logo" />
                </>
              ) : (
                <div className="no-images gmarket-font">첨부된 사진이 없습니다.</div>
              )}
            </div>
          </div>
        </div>

        <div className="record-modal-button-section">
          <Button className="modal-button" onClick={handleSaveImage}>
            리포트출력
          </Button>
          <Button className="modal-button" onClick={handleSMSModalOpen}>
            SMS전송
          </Button>
        </div>
      </Modal>

      <Modal opened={isSMSModalOpen} onClose={handleSMSModalClose} zIndex={9999} centered size="md">
        <p className="title-text">전화번호</p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
            marginTop: '5px',
          }}
        >
          <input
            type="text"
            placeholder="전화번호 입력"
            className="gray-input gmarket-font"
            maxLength={13}
            style={{ width: '100%' }}
            value={phoneNumber}
            onChange={handlePhoneChange}
          />
          <Button onClick={handleSMSSend} disabled={isSending} style={isSending ? { backgroundColor: '#ccc', cursor: 'not-allowed' } : {}} className="send-button">
            {isSending ? '전송중...' : '전송'}
          </Button>
        </div>
      </Modal>

      {mobileFilterModalOpen && (
        <Modal
          opened={mobileFilterModalOpen}
          onClose={() => setMobileFilterModalOpen(false)}
          zIndex={9999}
          centered={false}
          size="md"
          styles={{
            content: {
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              margin: 0,
              borderRadius: '20px 20px 0 0',
              transform: 'translateY(0)',
              maxHeight: '100vh',
              overflow: 'hidden',
              animation: mobileFilterModalOpen ? 'slideUp 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)' : 'slideDown 0.3s ease-in',
            },
            body: { padding: '20px', maxHeight: '100vh', overflowY: 'auto' },
            header: { display: 'none' },
          }}
          transitionProps={{ transition: 'fade', duration: 0 }}
        >
          <div>
            <div>
              <p className="gmarket-font">시작일</p>
              <div>
                <MantineDatePicker
                  value={startDate}
                  onChange={setStartDate}
                  className="mobile-date-picker"
                  comboboxProps={{
                    withinPortal: true,
                    zIndex: 10002,
                    position: 'bottom-start',
                  }}
                  popoverProps={{
                    withinPortal: true,
                    zIndex: 10002,
                    position: 'bottom-start',
                    styles: { dropdown: { width: '350px', maxWidth: '350px' } },
                  }}
                  styles={{ levelsGroup: { display: 'flex', justifyContent: 'center', width: '100%' } }}
                />
                <p className="gmarket-font">종료일</p>
                <MantineDatePicker
                  value={endDate}
                  onChange={setEndDate}
                  className="mobile-date-picker"
                  comboboxProps={{
                    withinPortal: true,
                    zIndex: 10002,
                    position: 'bottom-start',
                  }}
                  popoverProps={{
                    withinPortal: true,
                    zIndex: 10002,
                    position: 'bottom-start',
                    styles: { dropdown: { width: '350px', maxWidth: '350px' } },
                  }}
                  styles={{ levelsGroup: { display: 'flex', justifyContent: 'center', width: '100%' } }}
                />
              </div>
            </div>

            <div>
              <p className="gmarket-font">태그NO</p>
              <input
                placeholder="태그NO"
                value={tagNo}
                onChange={e => setTagNo(e.target.value)}
                style={{
                  paddingLeft: '20px',
                  width: '100%',
                  height: '50px',
                  borderRadius: '3px',
                  border: '1px solid #ABABAB',
                  padding: '12px 12px 12px 40px',
                  color: '#333',
                }}
              />
            </div>

            <div>
              <p className="gmarket-font">회원명</p>
              <input
                placeholder="회원명"
                value={userName}
                onChange={e => setUserName(e.target.value)}
                style={{
                  paddingLeft: '20px',
                  width: '100%',
                  height: '50px',
                  borderRadius: '3px',
                  border: '1px solid #ABABAB',
                  padding: '12px 12px 12px 40px',
                  color: '#333',
                }}
              />
            </div>

            <div>
              <p className="gmarket-font">사업장명</p>
              <input
                placeholder="사업장명"
                value={siteName}
                onChange={e => setSiteName(e.target.value)}
                style={{
                  paddingLeft: '20px',
                  width: '100%',
                  height: '50px',
                  borderRadius: '3px',
                  border: '1px solid #ABABAB',
                  padding: '12px 12px 12px 40px',
                  color: '#333',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p className="gmarket-font">상태</p>
              <MantineSelectBox
                style={{ width: '100%' }}
                value={status}
                onChange={setStatus}
                comboboxProps={{
                  withinPortal: true,
                  zIndex: 10002,
                  position: 'bottom-start',
                }}
                options={stateOptions}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                onClick={() => setMobileFilterModalOpen(false)}
                style={{
                  flex: 1,
                  height: '50px',
                  backgroundColor: '#f4f5f6',
                  color: '#333',
                  borderRadius: '10px',
                }}
              >
                취소
              </Button>
              <Button
                onClick={handleMobileFilterSearch}
                style={{
                  flex: 1,
                  height: '50px',
                  borderRadius: '10px',
                }}
              >
                찾기
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <Alert isOpen={alertOpen.isOpen} message={alertOpen.message} onConfirm={closeAlert} />

      <div ref={reportRef} id="report-print">
        <div className="record-modal-container">
          <div className="modal-section">
            <div className="modal-background-wrapper">
              <span className="modal-big-title">DAEDEOK PATROL REPORT</span>
              <div className="info-display">
                <InfoDisplay label="순찰시간" value={recordDetail?.memoRow?.REG_DTM} />
                <InfoDisplay label="태크명" value={recordDetail?.memoRow?.TAGNUM} />
                <InfoDisplay label="사업장명" value={recordDetail?.memoRow?.STORE_NAME} />
                <InfoDisplay label="위치장소" value={recordDetail?.memoRow?.STORE_ADDR} />
              </div>
            </div>

            <div className="modal-background-wrapper">
              <div className="title-logo-wrapper">
                <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
                <p className="report-modal-title gmarket-font">점검결과 및 내용</p>
              </div>
              <div className="checklist-items-wrapper">
                {recordDetail?.checklistData?.length > 0 ? (
                  recordDetail.checklistData.map((item, index) => (
                    <div key={index} className="checklist-item">
                      <div className="checklist-header">
                        <div className={`custom-checkbox ${item.checked ? 'checked' : ''}`}>
                          {item.checked && <span className="checkmark"></span>}
                        </div>
                        <span className="checklist-content">{item.content}</span>
                      </div>
                      {item.reason && (
                          <div className="checklist-reason">
                            <strong>사유: </strong>
                            {item.reason}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-data gmarket-font">점검사항이 없습니다.</div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-background-wrapper">
            <div className="title-logo-wrapper">
              <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
              <p className="report-modal-title gmarket-font">전달사항 및 의견</p>
            </div>
            <div className="special-notes">{recordDetail?.memoRow ? recordDetail.memoRow.MEMO : <div className="no-images gmarket-font">특이사항이 없습니다.</div>}</div>
          </div>

          <div className="modal-background-wrapper">
            <div className="title-logo-wrapper">
              <img src="/assets/images/dd-logo.png" alt="로고" className="report-logo" />
              <p className="report-modal-title gmarket-font">점검 증빙사진 (1장)</p>
            </div>
            <div className="attached-images">
              {recordDetail?.attachedImages?.length > 0 ? (
                <>
                  {recordDetail.attachedImages.map((image, index) => (
                    <div key={index} className="image-item">
                      <img
                        src={`/public${image.url || image}`}
                        alt={`첨부사진 ${index + 1}`}
                        className="attached-image"
                        onClick={() => window.open(`/public${image.url || image}`, '_blank')}
                      />
                    </div>
                  ))}
                  <img src="/assets/images/dd-korean-logo.png" alt="로고" className="report-korean-logo" />
                </>
              ) : (
                <div className="no-images gmarket-font">첨부된 사진이 없습니다.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordInfo;
